import { ReactNode } from "react";

import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Stack } from "@mui/material";
import { QuestionType, TicketingFormCategory } from "@simplyk/common";

import { QuestionCard } from "./QuestionCard/QuestionCard";
import { QuestionSection } from "./QuestionSection/QuestionSection";
import { getStepQuestionsV4Wording } from "./helpers/getStepQuestionsV4Wording";
import { getQuestionTypeSubtitle } from "./helpers/questionTypeIcons";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

interface BaseQuestion {
  localId: string;
  body: string;
  type: QuestionType;
  required: boolean;
  choices?: { body: string }[];
  scope: "buyer" | "participant";
}

interface TicketingLinkedQuestionsListV4Props<TQuestion extends BaseQuestion> {
  formCategory?: TicketingFormCategory;
  buyerQuestions: TQuestion[];
  attendeeQuestions: TQuestion[];
  onReorderBuyer: (event: DragEndEvent) => void;
  onReorderAttendee: (event: DragEndEvent) => void;
  onEdit: (question: TQuestion) => void;
  onDelete: (localId: string) => void;
  onAddBuyerQuestion: () => void;
  onAddAttendeeQuestion: () => void;
  askForAddress: boolean;
  onDeleteAddress: () => void;
  getQuestionIndex: (question: TQuestion) => number;
  getDeleteDisabledTooltip?: (question: TQuestion) => string | undefined;
  getLabelTooltip?: (question: TQuestion) => string | undefined;
  getQuestionLabel?: (question: TQuestion) => string | undefined;
  attendeeSuggestions?: ReactNode;
  /** Non-question cards rendered under the buyer questions (e.g. Communication Preferences). */
  additionalBuyerOptions?: ReactNode;
}

export const TicketingLinkedQuestionsListV4 = <TQuestion extends BaseQuestion>({
  formCategory,
  buyerQuestions,
  attendeeQuestions,
  onReorderBuyer,
  onReorderAttendee,
  onEdit,
  onDelete,
  onAddBuyerQuestion,
  onAddAttendeeQuestion,
  askForAddress,
  onDeleteAddress,
  getQuestionIndex,
  getDeleteDisabledTooltip,
  getLabelTooltip,
  getQuestionLabel,
  attendeeSuggestions,
  additionalBuyerOptions,
}: TicketingLinkedQuestionsListV4Props<TQuestion>) => {
  const { t } = useTranslate();
  const wording = getStepQuestionsV4Wording(t, formCategory);

  const isBuyerSectionReorderable = buyerQuestions.length > 1;
  const isAttendeeSectionReorderable = attendeeQuestions.length > 1;

  const renderQuestionCards = (questions: TQuestion[], isSectionReorderable: boolean) =>
    questions.map((question) => (
      <QuestionCard
        key={question.localId}
        label={getQuestionLabel?.(question) ?? question.body}
        subtitle={getQuestionTypeSubtitle(t, question.type, question.choices)}
        required={question.required}
        sortableId={isSectionReorderable ? question.localId : undefined}
        onEdit={() => onEdit(question)}
        onDelete={() => onDelete(question.localId)}
        deleteDisabledTooltip={getDeleteDisabledTooltip?.(question)}
        labelTooltip={getLabelTooltip?.(question)}
        questionIndex={getQuestionIndex(question)}
        questionScope={question.scope}
      />
    ));

  const attendeeSection = (
    <QuestionSection
      key="attendee"
      vibe="brand"
      title={wording.attendeeSectionTitle}
      titleTooltip={wording.attendeeSectionTooltip}
      addButtonLabel={wording.addAttendeeQuestionLabel}
      onAddQuestion={onAddAttendeeQuestion}
      addButtonTestId="v4-add-guest-question-button"
      emptyState={
        attendeeQuestions.length === 0 &&
        !attendeeSuggestions && (
          <DashboardTypography variant="body2" sx={{ color: "text.neutral.supershy" }}>
            {wording.attendeeSectionEmpty}
          </DashboardTypography>
        )
      }
    >
      {attendeeQuestions.length > 0 && (
        <DndContext collisionDetection={closestCenter} onDragEnd={onReorderAttendee}>
          <SortableContext items={attendeeQuestions.map((q) => q.localId)} strategy={verticalListSortingStrategy}>
            {renderQuestionCards(attendeeQuestions, isAttendeeSectionReorderable)}
          </SortableContext>
        </DndContext>
      )}
      {attendeeSuggestions}
    </QuestionSection>
  );

  const buyerSection = (
    <QuestionSection
      key="buyer"
      title={wording.buyerSectionTitle}
      titleTooltip={wording.buyerSectionTooltip}
      addButtonLabel={wording.addBuyerQuestionLabel}
      onAddQuestion={onAddBuyerQuestion}
      addButtonTestId="v4-add-buyer-question-button"
    >
      <QuestionCard
        label={t("dashboard", "stepQuestionsV4.defaultBuyerName")}
        subtitle={t("dashboard", "stepQuestionsV4.typeShortText")}
        required
        showDisabledReorderHandle={isBuyerSectionReorderable}
        deleteDisabledTooltip={t("dashboard", "stepQuestionsV4.defaultQuestionTooltip")}
      />
      <QuestionCard
        label={t("dashboard", "email")}
        subtitle={t("dashboard", "donationForm.questionEmail")}
        required
        showDisabledReorderHandle={isBuyerSectionReorderable}
        deleteDisabledTooltip={t("dashboard", "stepQuestionsV4.defaultQuestionTooltip")}
      />
      {askForAddress ? (
        <QuestionCard
          label={t("dashboard", "fullAddress")}
          subtitle={t("dashboard", "stepQuestionsV4.dialog.typeAddress")}
          required
          showDisabledReorderHandle={isBuyerSectionReorderable}
          onDelete={onDeleteAddress}
        />
      ) : (
        <QuestionCard
          label={t("dashboard", "stateAndCountry")}
          subtitle={t("dashboard", "donationForm.uniqueQuestion")}
          required
          showDisabledReorderHandle={isBuyerSectionReorderable}
          deleteDisabledTooltip={t("dashboard", "stepQuestionsV4.defaultQuestionTooltip")}
        />
      )}

      {buyerQuestions.length > 0 && (
        <DndContext collisionDetection={closestCenter} onDragEnd={onReorderBuyer}>
          <SortableContext items={buyerQuestions.map((q) => q.localId)} strategy={verticalListSortingStrategy}>
            {renderQuestionCards(buyerQuestions, isBuyerSectionReorderable)}
          </SortableContext>
        </DndContext>
      )}

      {additionalBuyerOptions}
    </QuestionSection>
  );

  return (
    <Stack spacing={5}>
      {wording.isBuyerSectionFirst ? [buyerSection, attendeeSection] : [attendeeSection, buyerSection]}
    </Stack>
  );
};
